import { Snackbar as MuiSnackbar, SnackbarProps } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Close, ErrorOutline } from '@material-ui/icons';
import clsx from 'clsx';
import React from 'react';
import { IconButton } from '.';
import { theme } from '../../theme/theme';

const useSnackBarClasses = makeStyles({
    root: {
        display: 'flex',
        alignItems: 'center',
        minWidth: 290,
        height: 56,
        padding: '0 18px',
        borderRadius: 4,
        color: theme.colors.white,
        '&.error': {
            background: theme.colors.red[1],
            ...theme.typography.content.label.strong,
        },
        '&.info': {
            background: theme.colors.neutral[1],
            ...theme.typography.content.label.default,
        },
        '& > svg': {
            width: 20,
            height: 20,
            marginRight: 15,
        },
        '& > button': {
            padding: 5,
            color: 'inherit',
        },
    },
});

/**
 * Themed material-ui snackbar.
 * Themes are applied according to `props.severity`.
 * This component also implements an extra functionality for adding a close button.
 * Usually snack bars are used only once, so they start as open by default and actions lead it to the close state.
 * Snack bars can be reused by setting `props.startClosed` and `props.onSetOpen` properties.
 *
 * @param props.snackbarProps snackbar properties
 * @param props.severity snackbar severity
 * @param props.startClosed start the snackbar closed rather than opened
 * @param props.onSetOpen reference to extract setOpen snackbar set open, provided during layout effect
 */
export const SnackBar = (
    props: JSX.IntrinsicElements['div'] & {
        snackbarProps: SnackbarProps;
        severity?: 'error' | 'info';
        startClosed?: boolean;
        onSetOpen?: (setOpen: (open: boolean) => void) => void;
    },
) => {
    const { snackbarProps, severity = 'info', startClosed, onSetOpen, ...rootProps } = props;
    const classes = useSnackBarClasses();
    const [open, setOpen] = React.useState(!startClosed);
    const cls = { [severity]: true };

    React.useLayoutEffect(() => onSetOpen?.(setOpen));

    return (
        <MuiSnackbar
            {...snackbarProps}
            open={open}
            onClose={(event, reason) => (setOpen(false), snackbarProps?.onClose?.(event, reason))}
        >
            <div {...rootProps} className={clsx(classes.root, cls, rootProps.className)}>
                {severity === 'error' && <ErrorOutline />}
                {rootProps.children}
                <div style={{ flex: 1 }} />
                <IconButton onClick={event => (setOpen(false), snackbarProps?.onClose?.(event, 'clickaway'))}>
                    <Close />
                </IconButton>
            </div>
        </MuiSnackbar>
    );
};
